import { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PageListContainer, PageCreateButton } from "../../../components/Page";
import { useConfirm } from "../../../components/feedback";
import DataTableWithPagination from "../../../components/DataTable";
import ProjectDialog from "../../../components/GenericDialog";

import { intentRuleService } from "../../../services/intentRuleService";
import IntentRuleModel, { getIntentRuleCols } from "../model";

import createIntentRuleBody from "./components/CreateIntentRuleDialogBody";
import editIntentRuleBody from "./components/EditIntentRuleDialogBody";

function IntentRulesPage() {
  const { t } = useTranslation();
  const cols = useMemo(() => getIntentRuleCols(t), [t]);
  const datatable = useRef<any>(null);
  const createIntentRuleDialogRef = useRef<any>(null);
  const editIntentRuleDialogRef = useRef<any>(null);

  const confirm = useConfirm();

  useEffect(() => {
    const handleGlobalRefresh = () => {
      if (datatable.current) datatable.current.refresh();
    };
    window.addEventListener("refreshDataTable", handleGlobalRefresh);
    return () => window.removeEventListener("refreshDataTable", handleGlobalRefresh);
  }, []);

  const showSnack = (opts: any) => {
    window.dispatchEvent(new CustomEvent("showGlobalNotification", { detail: opts }));
  };

  const parseExamples = (raw: any): string[] => {
    const s = String(raw ?? "");
    return s
      .split(/\r?\n|,/g)
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const parseDslHint = (raw: any): Record<string, any> => {
    const s = String(raw ?? "").trim();
    if (!s) return {};
    try {
      const parsed = JSON.parse(s);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      console.error("Invalid dslHint JSON:", e);
      return {};
    }
  };

  const createNewIntentRule = () => {
    if (createIntentRuleDialogRef.current) {
      createIntentRuleDialogRef.current.showDialog(IntentRuleModel.createDialogOpts(t));
    }
  };

  const createIntentRuleSubmitAction = (formJson: any) => {
    intentRuleService
      .createIntentRule({
        intent: formJson.intent,
        userQueryExamples: parseExamples(formJson.userQueryExamples),
        analysisRule: {
          targetIndex: formJson.targetIndex,
          filterField: formJson.filterField,
          searchType: formJson.searchType,
          searchValue: formJson.searchValue,
          timeField: formJson.timeField,
          timeRange: formJson.timeRange,
          aggregationType: formJson.aggregationType,
        },
        dslHint: parseDslHint(formJson.dslHint),
      })
      .then(() => {
        IntentRuleModel.message(
          IntentRuleModel.SUCCESS,
          t("common.createRequest"),
          t("intentRules.createdSuccess"),
          Date.now
        ).then((o) => {
          showSnack(o);
          setTimeout(() => datatable.current?.refresh(), 500);
        });
      })
      .catch((e: unknown) => console.error(e));
  };

  const editIntentRuleSubmitAction = (formJson: any) => {
    intentRuleService
      .updateIntentRule({
        id: formJson.id,
        intent: formJson.intent,
        userQueryExamples: parseExamples(formJson.userQueryExamples),
        analysisRule: {
          targetIndex: formJson.targetIndex,
          filterField: formJson.filterField,
          searchType: formJson.searchType,
          searchValue: formJson.searchValue,
          timeField: formJson.timeField,
          timeRange: formJson.timeRange,
          aggregationType: formJson.aggregationType,
        },
        dslHint: parseDslHint(formJson.dslHint),
      })
      .then(() => {
        IntentRuleModel.message(
          IntentRuleModel.SUCCESS,
          t("common.updateRequest"),
          t("intentRules.updatedSuccess"),
          Date.now
        ).then((o) => {
          showSnack(o);
          setTimeout(() => datatable.current?.refresh(), 500);
        });
      })
      .catch((e: unknown) => console.error(e));
  };

  const handleDelete = async (item: any) => {
    return new Promise((resolve) => {
      confirm({
        description: t("intentRules.deleteConfirm", { name: item.intent }),
      })
        .then(() => {
          intentRuleService
            .deleteIntentRule(item.id)
            .then(() => resolve({}));
        })
        .then(() => {
          IntentRuleModel.message(
            IntentRuleModel.SUCCESS,
            t("common.deleteRequest"),
            t("intentRules.deletedSuccess"),
            Date.now
          ).then((opts) => {
            showSnack(opts);
            setTimeout(() => datatable.current?.refresh(), 500);
          });
        })
        .catch((e: unknown) => console.error(e));
    });
  };

  const handleEdit = async (item: any) => {
    if (editIntentRuleDialogRef.current) {
      editIntentRuleDialogRef.current.showDialog(IntentRuleModel.editDialogOpts(t), item);
    }
  };

  return (
    <PageListContainer>
      <ProjectDialog
        ref={createIntentRuleDialogRef}
        submitAction={createIntentRuleSubmitAction}
        body={createIntentRuleBody}
        maxWidth="md"
        fullWidth
      />
      <ProjectDialog
        ref={editIntentRuleDialogRef}
        submitAction={editIntentRuleSubmitAction}
        body={editIntentRuleBody}
        maxWidth="md"
        fullWidth
      />

      <DataTableWithPagination
        ref={datatable}
        cols={cols}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        fetchUrl={"/intent-rule/read"}
        searchKey={"intent"}
        headerActions={
          <PageCreateButton onClick={createNewIntentRule}>{t("intentRules.new")}</PageCreateButton>
        }
      />
    </PageListContainer>
  );
}

export default IntentRulesPage;

